import React, { useCallback, useEffect, useState } from 'react';

import { docTypes, idDocs } from 'utils/constants';
import { texts } from 'utils/languages/fr';

import {
    ActionButton,
    CancelButton,
    CustomDialogTitle,
    CustomSelect,
    FileErrorMessage,
    FileInputButton,
} from 'components';
import { Box, Dialog, DialogActions, DialogContent, MenuItem, Typography } from '@mui/material';
import { checkImageFile, getFileExtension, validateFileSize } from 'utils/helpers';
import { useReplaceDocumentMutation } from 'api/main';

const renderOption = opt => (
    <MenuItem key={opt.value} value={opt.value}>
        {opt.label}
    </MenuItem>
);

const IdReplaceDialog = props => {
    const { id, open, handleClose, title = texts.REMPLACER } = props;

    const [form, setForm] = useState({
        selectedFile: null,
        fileType: '',
    });
    const [typeError, setTypeError] = useState('');
    const [fileError, setFileError] = useState();
    const [replaceDocument, { isLoading, isSuccess, reset }] = useReplaceDocumentMutation();

    const handleFileChange = useCallback(e => {
        const file = e.target.files[0];
        let isImage = false;
        const fileExtention = getFileExtension(file);
        checkImageFile(file, () => (isImage = true));
        if (fileExtention === 'pdf' || isImage) {
            if (validateFileSize(file)) {
                setForm(prevState => ({ ...prevState, selectedFile: file }));
                setFileError('');
            } else {
                setFileError('size');
            }
        } else {
            setFileError('type');
        }
    }, []);

    const handleTypeChange = useCallback(e => {
        setForm(prevState => ({ ...prevState, fileType: e.target.value }));
    }, []);

    const handleSubmit = useCallback(() => {
        if (!form.fileType) {
            setTypeError(texts.VEUILLEZ_CHOISIR_UNE_OPTION);
        }
        if (form.selectedFile && form.fileType) {
            const formData = new FormData();
            formData.append('file', form.selectedFile);
            formData.append('type', docTypes.ID);
            formData.append('subtype', form.fileType);
            replaceDocument({ id, formData });
        }
    }, [replaceDocument, form, id]);

    useEffect(() => {
        if (isSuccess) {
            handleClose();
        }
    }, [isSuccess, handleClose]);

    useEffect(() => {
        if (!open) {
            reset();
            setForm({
                selectedFile: null,
                fileType: '',
            });
        }
    }, [open, reset]);

    return (
        <Dialog open={open}>
            <CustomDialogTitle title={title} handleClose={handleClose} />
            <DialogContent>
                <CustomSelect
                    label={texts.DOCUMENT_TYPE}
                    value={form.fileType}
                    onChange={handleTypeChange}
                    error={!!typeError}
                    helperText={typeError}
                >
                    {idDocs.map(renderOption)}
                </CustomSelect>
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    flexDirection="column"
                >
                    {form.selectedFile && <Typography>{form.selectedFile.name}</Typography>}
                    <FileInputButton
                        id="id-replacement"
                        onFileChange={handleFileChange}
                        label={texts.UPLOAD_FILE_BUTTON}
                    />
                    {!!fileError && <FileErrorMessage variant={fileError} useMargin={false} />}
                </Box>
            </DialogContent>
            <DialogActions>
                <CancelButton disabled={isLoading} onClick={handleClose}>
                    {texts.CANCEL}
                </CancelButton>
                <ActionButton disabled={isLoading} onClick={handleSubmit} loading={isLoading}>
                    {texts.SAVE}
                </ActionButton>
            </DialogActions>
        </Dialog>
    );
};

export default IdReplaceDialog;
